<script setup lang="ts">
    import {
        IInteraction,
        IInteractionTranscriptionSegment,
    } from '@/js/services/api/interaction/types';
    import { __ } from '@/js/stores/expressions';
    import TranscriptionChatMessage from './TranscriptionChatMessage.vue';
    import { computed, ref, watch } from 'vue';
    import { useSharedFilterStore } from '@/js/stores/filter';
    import { useAudioPlayerStore } from '@/js/stores/audioPlayer';
    import { textMatchesKeywords } from '@/js/utils/keywordMatch';
    import {
        estimateSegmentDurations,
        getTranscriptionDuration,
    } from '@/js/utils/transcriptionSegments';
    import EchoButton from '../../EchoButton.vue';

    const transcriptionChat = ref<HTMLElement | null>(null);

    const props = withDefaults(
        defineProps<{
            interaction: IInteraction;
            currentTime: number;
            // Set to false in contexts without a search form (e.g. the standalone
            // interaction page) so the transcript is never filtered/highlighted.
            enableKeywordFilter?: boolean;
        }>(),
        { enableKeywordFilter: true }
    );

    const filterStore = useSharedFilterStore();
    const audioPlayerStore = useAudioPlayerStore();

    // Whether a keyword search is currently active.
    const hasKeywordFilter = computed(
        () =>
            props.enableKeywordFilter &&
            (filterStore.all?.keywordFilter?.words ?? []).filter((w) => w.trim().length > 0)
                .length > 0
    );

    // When true, all messages are shown regardless of the keyword filter.
    const showAll = ref(false);

    // Reset the "show all" toggle whenever the search changes.
    watch(
        () => filterStore.all?.keywordFilter?.words,
        () => (showAll.value = false),
        { deep: true }
    );

    const matchesKeyword = (segment: IInteractionTranscriptionSegment) =>
        textMatchesKeywords(segment.text, filterStore.all?.keywordFilter);

    const matchingCount = computed(
        () => props.interaction.transcription.filter(matchesKeyword).length
    );

    // Number of messages filtered away by the current search.
    const hiddenCount = computed(
        () => props.interaction.transcription.length - matchingCount.value
    );

    const isVoiceInteraction = computed(() => props.interaction.channelType === 0);

    const duration = computed(() =>
        getTranscriptionDuration(
            props.interaction.transcription,
            isVoiceInteraction.value,
            props.interaction.summary?.audioFileLength || 0
        )
    );

    // Segments with an estimated duration filled in where the real one is
    // missing, the same estimate TranscriptionVisualization draws for the audio
    // bar, so the highlight stays active for a segment's whole width, not a sliver.
    const timedSegments = computed(() =>
        estimateSegmentDurations(
            props.interaction.transcription,
            duration.value,
            isVoiceInteraction.value
        )
    );

    // The segment currently playing, synced with the audio player. Compared at
    // full precision, not whole seconds: some segments are under a second long,
    // so a whole-second compare could match only the first of two in one second.
    const activeSegmentId = computed(() => {
        const time = props.currentTime;
        if (time <= 0) return null;

        const active = timedSegments.value.find(
            (segment) => time >= segment.segmentStart && time < segment.segmentEnd
        );

        return active?.segmentId ?? null;
    });

    type DisplayItem =
        | { type: 'message'; key: string; message: IInteractionTranscriptionSegment }
        | { type: 'hidden'; key: string; messages: IInteractionTranscriptionSegment[] };

    // Builds the render list, collapsing consecutive non-matching messages into
    // a single "hidden messages" group when a keyword filter is active.
    const displayItems = computed<DisplayItem[]>(() => {
        const segments = props.interaction.transcription;

        // When the search matches nothing, there is nothing to filter "to" —
        // fall back to showing the whole transcript.
        if (!hasKeywordFilter.value || showAll.value || matchingCount.value === 0) {
            return segments.map((message) => ({
                type: 'message',
                key: message.segmentId,
                message,
            }));
        }

        const items: DisplayItem[] = [];
        let hiddenBuffer: IInteractionTranscriptionSegment[] = [];

        const flush = () => {
            if (hiddenBuffer.length > 0) {
                items.push({
                    type: 'hidden',
                    key: 'hidden-' + hiddenBuffer[0].segmentId,
                    messages: hiddenBuffer,
                });
                hiddenBuffer = [];
            }
        };

        for (const message of segments) {
            if (matchesKeyword(message)) {
                flush();
                items.push({ type: 'message', key: message.segmentId, message });
            } else {
                hiddenBuffer.push(message);
            }
        }
        flush();

        return items;
    });

    const automaticScrollPaused = ref(false);
    const hasAudio = computed(
        () => isVoiceInteraction.value && (props.interaction.summary?.audioFileLength ?? 0) > 0
    );

    const isAutomaticScroll = ref(false);
    const pausedScrollTimeout = ref<number | undefined>();

    const handleScroll = () => {
        if (isAutomaticScroll.value) {
            return;
        }

        // Pause automatic scrolling for 5 seconds after user scrolls manually
        automaticScrollPaused.value = true;

        if (pausedScrollTimeout.value) {
            clearTimeout(pausedScrollTimeout.value);
        }

        // @ts-ignore
        pausedScrollTimeout.value = window.setTimeout(() => {
            isAutomaticScroll.value = false;
            automaticScrollPaused.value = false;
        }, 5000);
    };

    // Don't auto-scroll while a keyword filter is hiding messages — the
    // transcript order no longer matches the audio timeline. Once "show all"
    // is active the full transcript is back, so scrolling is allowed again.
    const canAutoScroll = computed(
        () =>
            !automaticScrollPaused.value &&
            audioPlayerStore.autoScrollEnabled &&
            (!hasKeywordFilter.value || showAll.value)
    );

    const scrollToTop = () => {
        if (!canAutoScroll.value || !transcriptionChat.value) return;

        isAutomaticScroll.value = true;
        transcriptionChat.value.scrollTop = 0;
    };

    const scrollToActiveMessage = (segmentId: string | null) => {
        if (!segmentId || !canAutoScroll.value || !transcriptionChat.value) return;

        isAutomaticScroll.value = true;

        const activeChatMessage = transcriptionChat.value.querySelector(
            `[data-segment-id="${segmentId}"]`
        ) as HTMLElement | null;

        if (activeChatMessage) {
            transcriptionChat.value.scrollTo({
                top: activeChatMessage.offsetTop - 16,
                behavior: 'smooth',
            });
        }
    };

    const handleScrollEnd = () => {
        isAutomaticScroll.value = false;
    };

    watch(
        () => Math.floor(props.currentTime),
        (value) => {
            if (value === 0) scrollToTop();
        }
    );

    watch(activeSegmentId, (segmentId) => scrollToActiveMessage(segmentId));
</script>
<template>
    <div class="tc-wrap">
        <div class="tc-inner">
            <div
                v-if="hasKeywordFilter && matchingCount > 0 && hiddenCount > 0"
                class="tc-match-banner"
            >
                <p>
                    {{
                        __(
                            matchingCount === 1
                                ? 'transcription-matching-count-one'
                                : 'transcription-matching-count',
                            { count: String(matchingCount) }
                        )
                    }}
                </p>

                <EchoButton size="small" @click="showAll = !showAll">
                    {{ showAll ? __('show-matching') : __('show-all') }}
                </EchoButton>
            </div>
            <div class="chat-cols">
                <h2 class="chat-col-label pl-3">
                    <span class="size-2 rounded-full bg-gray-extralight border border-gray-light" />
                    {{ __('customer') }}
                </h2>
                <h2 class="chat-col-label">
                    {{ __('agent') }}
                    <span class="size-2 rounded-full bg-white border border-gray-light" />
                </h2>
            </div>
            <div
                ref="transcriptionChat"
                class="chat-scroll custom-scrollbar"
                style="--scrollbar-track: transparent"
                @scrollend="handleScrollEnd()"
                @scroll="handleScroll()"
            >
                <template v-for="item in displayItems" :key="item.key">
                    <TranscriptionChatMessage
                        v-if="item.type === 'message'"
                        :agent-name="interaction.agentName"
                        :interaction-id="interaction.id"
                        :message="item.message"
                        :is-match="
                            (hasKeywordFilter && matchesKeyword(item.message)) ||
                            item.message.segmentId === activeSegmentId
                        "
                        :highlight="hasKeywordFilter"
                        :can-listen="hasAudio"
                    />
                    <div v-else class="tc-hidden-messages">
                        {{
                            __(
                                item.messages.length === 1
                                    ? 'hidden-message-count-one'
                                    : 'hidden-message-count',
                                { count: String(item.messages.length) }
                            )
                        }}
                    </div>
                </template>
            </div>
        </div>
    </div>
</template>
